if (!customElements.get('manzanita-finder')) {
  customElements.define('manzanita-finder', class extends HTMLElement {
    async connectedCallback() {
      this.grid = this.querySelector('[data-product-grid]');
      this.status = this.querySelector('[data-status]');
      this.filters = {};
      try {
        const [catalogResponse, evidenceResponse] = await Promise.all([
          fetch(this.dataset.productsUrl),
          fetch(this.dataset.evidenceUrl),
        ]);
        if (!catalogResponse.ok || !evidenceResponse.ok) throw new Error('Product data unavailable');
        const catalog = await catalogResponse.json();
        const evidence = await evidenceResponse.json();
        const evidenceByHandle = new Map(evidence.products.map((item) => [item.handle, item]));
        const dancingOaksSelectionOverrides = new Map([
          ['arctostaphylos-blue-bear-ecy-manzanita', 'ECY'],
          ['arctostaphylos-hybrid-siskiyou-pink-x-austin-griffiths-manzanita', ''],
          ['arctostaphylos-ect-blue-fuzzy-manzanita', 'ECT'],
        ]);
        this.products = catalog.products.map((product, index) => {
          const evidenceItem = evidenceByHandle.get(product.handle) || {};
          const isSelectionOverride = dancingOaksSelectionOverrides.has(product.handle);
          return {
            ...product,
            ...evidenceItem,
            dancingOaks: isSelectionOverride || Boolean(evidenceItem.dancingOaks),
            collectionNumber: isSelectionOverride ? dancingOaksSelectionOverrides.get(product.handle) : evidenceItem.collectionNumber,
            featuredIndex: index,
          };
        });
        this.populateOptions();
        this.restoreUrlState();
        this.bindEvents();
        this.render();
      } catch (error) {
        this.status.textContent = 'The finder could not load.';
        this.grid.innerHTML = `<p class="manzanita__error">Please refresh the page or <a href="${location.pathname}?sort_by=title-ascending">browse the standard collection</a>.</p>`;
      }
    }

    escape(value = '') {
      const node = document.createElement('div');
      node.textContent = String(value);
      return node.innerHTML;
    }

    populateOptions() {
      const order = {
        height: ['Under 1 ft', '1–3 ft', 'Over 3–6 ft', 'Over 6–10 ft', 'Over 10 ft', 'Not yet recorded'],
        colors: ['White', 'Pink', 'White to pink', 'Pink to white', 'Not yet recorded'],
        bloom: ['Winter', 'Early spring', 'Mid spring', 'Spring', 'Late spring', 'Summer', 'Fall', 'Not yet recorded'],
      };
      Object.entries(order).forEach(([key, values]) => {
        const available = new Set(this.products.flatMap((product) => Array.isArray(product[key]) ? product[key] : [product[key]]));
        this.querySelectorAll(`[data-filter="${key}"]`).forEach((select) => {
          values.filter((value) => available.has(value)).forEach((value) => select.add(new Option(value, value)));
        });
      });
      this.populateBubbles(order);
    }

    populateBubbles(order) {
      const groups = {
        height: order.height,
        colors: order.colors,
        bloom: order.bloom,
      };
      Object.entries(groups).forEach(([key, values]) => {
        const available = new Set(this.products.flatMap((product) => Array.isArray(product[key]) ? product[key] : [product[key]]));
        const container = this.querySelector(`[data-bubble-group="${key}"]`);
        container.innerHTML = values.filter((value) => available.has(value)).map((value) => `<button type="button" class="manzanita__bubble" data-bubble-filter="${key}" data-bubble-value="${this.escape(value)}" aria-pressed="false"><span>${this.escape(value)}</span><small data-bubble-count></small></button>`).join('');
      });
      this.querySelector('[data-bubble-group="special"]').innerHTML = `
        <button type="button" class="manzanita__bubble manzanita__bubble--special" data-bubble-filter="dancingOaks" data-bubble-value="true" aria-pressed="false"><span>Dancing Oaks selections</span><small data-bubble-count></small></button>`;
    }

    restoreUrlState() {
      const params = new URLSearchParams(location.search);
      this.querySelectorAll('[data-filter]').forEach((select) => {
        const value = params.get(`mz_${select.dataset.filter}`) || '';
        if ([...select.options].some((option) => option.value === value)) select.value = value;
        this.filters[select.dataset.filter] = select.value;
      });
      ['height', 'colors', 'bloom', 'dancingOaks'].forEach((key) => {
        this.filters[key] = (params.get(`mz_${key}`) || '').split('|').filter(Boolean);
      });
      const sort = params.get('mz_sort');
      if (sort && [...this.querySelector('[data-sort]').options].some((option) => option.value === sort)) this.querySelector('[data-sort]').value = sort;
    }

    bindEvents() {
      this.querySelectorAll('[data-filter]').forEach((select) => select.addEventListener('change', () => {
        this.filters[select.dataset.filter] = select.value;
        this.querySelectorAll(`[data-filter="${select.dataset.filter}"]`).forEach((peer) => { peer.value = select.value; });
        this.render();
      }));
      this.querySelector('[data-sort]').addEventListener('change', () => this.render());
      this.querySelectorAll('[data-bubble-filter]').forEach((button) => button.addEventListener('click', () => {
        const key = button.dataset.bubbleFilter;
        const selected = new Set(this.filters[key] || []);
        selected.has(button.dataset.bubbleValue) ? selected.delete(button.dataset.bubbleValue) : selected.add(button.dataset.bubbleValue);
        this.filters[key] = [...selected];
        this.render();
      }));
      this.querySelector('[data-clear]').addEventListener('click', () => {
        this.filters = {};
        this.querySelectorAll('[data-filter]').forEach((select) => { select.value = ''; });
        this.render();
      });
      const toggle = this.querySelector('[data-mobile-filter]');
      toggle.addEventListener('click', () => {
        const panel = this.querySelector('[data-filter-panel]');
        const open = panel.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', String(open));
      });
    }

    render() {
      let products = this.products.filter((product) => Object.entries(this.filters).every(([key, value]) => {
        if (!value || (Array.isArray(value) && !value.length)) return true;
        if (key === 'availability') return product.variants.some((variant) => variant.available) === (value === 'true');
        const selected = Array.isArray(value) ? value : [value];
        if (key === 'dancingOaks') return selected.includes('true') && product.dancingOaks;
        const productValues = Array.isArray(product[key]) ? product[key] : [product[key]];
        return selected.some((item) => productValues.includes(item));
      }));
      const sort = this.querySelector('[data-sort]').value;
      const number = (product) => Number(product.variants.find((variant) => variant.available)?.price || product.variants[0]?.price || Infinity);
      products.sort((a, b) => sort === 'height-asc' ? (a.heightMax ?? Infinity) - (b.heightMax ?? Infinity)
        : sort === 'height-desc' ? (b.heightMax ?? -Infinity) - (a.heightMax ?? -Infinity)
        : sort === 'title' ? a.title.localeCompare(b.title)
        : sort === 'price' ? number(a) - number(b)
        : a.featuredIndex - b.featuredIndex);
      this.status.textContent = `${products.length} ${products.length === 1 ? 'Manzanita' : 'Manzanitas'}`;
      this.querySelector('[data-result-count]').textContent = `${products.length} Manzanitas to explore`;
      this.grid.innerHTML = products.length ? products.map((product) => this.card(product)).join('') : '<p class="manzanita__empty">No Manzanitas match those filters. Try removing one selection.</p>';
      this.renderChips();
      this.updateBubbles();
      this.updateUrl(sort);
      this.bindImageCycles();
    }

    updateBubbles() {
      this.querySelectorAll('[data-bubble-filter]').forEach((button) => {
        const selected = this.filters[button.dataset.bubbleFilter] || [];
        button.setAttribute('aria-pressed', String(selected.includes(button.dataset.bubbleValue)));
        const proposed = { ...this.filters, [button.dataset.bubbleFilter]: selected.includes(button.dataset.bubbleValue) ? selected : [...selected, button.dataset.bubbleValue] };
        const count = this.products.filter((product) => this.matches(product, proposed)).length;
        button.querySelector('[data-bubble-count]').textContent = count;
        button.disabled = count === 0 && !selected.includes(button.dataset.bubbleValue);
      });
    }

    matches(product, filters = this.filters) {
      return Object.entries(filters).every(([key, value]) => {
        if (!value || (Array.isArray(value) && !value.length)) return true;
        if (key === 'availability') return product.variants.some((variant) => variant.available) === (value === 'true');
        const selected = Array.isArray(value) ? value : [value];
        if (key === 'dancingOaks') return selected.includes('true') && product.dancingOaks;
        const productValues = Array.isArray(product[key]) ? product[key] : [product[key]];
        return selected.some((item) => productValues.includes(item));
      });
    }

    card(product) {
      const available = product.variants.filter((variant) => variant.available);
      const variants = available.length ? available : product.variants;
      const first = variants[0];
      const price = first ? `$${Number(first.price).toFixed(2)}` : 'Price unavailable';
      const size = first?.title && first.title !== 'Default Title' ? ` · ${first.title}` : '';
      const extra = available.length > 1 ? ` <strong>+ ${available.length - 1} ${available.length === 2 ? 'size' : 'sizes'}</strong>` : '';
      const flower = product.colors?.join(' / ') || 'Not yet recorded';
      const bloom = product.bloom?.join(' / ') || 'Not yet recorded';
      const unknown = (value) => /not yet recorded/i.test(value) ? ' manzanita-card__trait--unknown' : '';
      const images = (product.images || []).slice(0, 3).map((image) => image.src || image).filter(Boolean);
      const image = images[0] || product.image?.src || '';
      return `<article class="manzanita-card">
        <a class="manzanita-card__media" href="/products/${this.escape(product.handle)}" data-images='${this.escape(JSON.stringify(images))}'>
          ${image ? `<img src="${this.escape(image)}&width=700" alt="${this.escape(product.title)}" loading="lazy" width="700" height="650">` : ''}
          ${product.dancingOaks ? `<span class="manzanita-card__badge">DANCING OAKS${product.collectionNumber ? ` · ${this.escape(product.collectionNumber)}` : ''}</span>` : ''}
        </a>
        <div class="manzanita-card__body"><h3><a href="/products/${this.escape(product.handle)}">${this.escape(product.title)}</a></h3>
          <p class="manzanita-card__price">${available.length ? '' : 'Currently unavailable · '}${price}${this.escape(size)}${extra}</p>
          <div class="manzanita-card__traits">
            <div class="manzanita-card__trait${unknown(product.height)}">Mature height<b>${this.escape(product.height || 'Not yet recorded')}</b></div>
            <div class="manzanita-card__trait${unknown(flower)}">Flower<b>${this.escape(flower)}</b></div>
            <div class="manzanita-card__trait${unknown(bloom)}">Observed bloom<b>${this.escape(bloom)}</b></div>
            <div class="manzanita-card__trait${unknown(product.shade)}">Shade tolerance<b>${this.escape(product.shade || 'Not yet recorded')}</b></div>
          </div><a class="manzanita-card__link" href="/products/${this.escape(product.handle)}">View plant details →</a>
        </div></article>`;
    }

    renderChips() {
      const names = { availability: 'Available now', height: 'Height', colors: 'Bloom color', bloom: 'Bloom time', dancingOaks: 'Dancing Oaks selections' };
      const active = Object.entries(this.filters).flatMap(([key, value]) => {
        if (!value) return [];
        return (Array.isArray(value) ? value : [value]).map((item) => [key, item]);
      });
      const container = this.querySelector('[data-active-filters]');
      container.innerHTML = active.map(([key, value]) => `<button class="manzanita__chip" type="button" data-remove="${key}" data-remove-value="${this.escape(value)}">${names[key]}: ${this.escape(value === 'true' ? 'Yes' : value)} ×</button>`).join('');
      container.querySelectorAll('[data-remove]').forEach((button) => button.addEventListener('click', () => {
        const key = button.dataset.remove;
        this.filters[key] = Array.isArray(this.filters[key]) ? this.filters[key].filter((value) => value !== button.dataset.removeValue) : '';
        this.querySelectorAll(`[data-filter="${key}"]`).forEach((select) => { select.value = ''; });
        this.render();
      }));
    }

    updateUrl(sort) {
      const params = new URLSearchParams();
      Object.entries(this.filters).forEach(([key, value]) => {
        const serialized = Array.isArray(value) ? value.join('|') : value;
        if (serialized) params.set(`mz_${key}`, serialized);
      });
      if (sort !== 'featured') params.set('mz_sort', sort);
      history.replaceState({}, '', `${location.pathname}${params.size ? `?${params}` : ''}`);
    }

    bindImageCycles() {
      if (matchMedia('(hover: none), (prefers-reduced-motion: reduce)').matches) return;
      this.grid.querySelectorAll('[data-images]').forEach((media) => {
        let images;
        try { images = JSON.parse(media.dataset.images); } catch { return; }
        if (images.length < 2) return;
        let timer; let index = 0;
        const advance = () => {
          index = (index + 1) % images.length;
          media.querySelector('img').src = `${images[index]}&width=700`;
          timer = setTimeout(advance, 2200);
        };
        media.addEventListener('mouseenter', () => {
          clearTimeout(timer);
          timer = setTimeout(advance, 1500);
        });
        media.addEventListener('mouseleave', () => {
          clearTimeout(timer);
          index = 0;
          media.querySelector('img').src = `${images[0]}&width=700`;
        });
      });
    }
  });
}
